"use client";

import * as React from "react";
import {
    OPEN_CONTEXT_MENU_EVENT,
    ContextMenuItem,
} from "@/utils/context-menu";
import {
    IconCaretRight as ChevronRight,
    IconCopy,
} from "@/app/components/icons";
import { cn } from "@/lib/utils";

interface MenuState {
    items: ContextMenuItem[] | null;
    position: { x: number; y: number };
    visible: boolean;
}

interface MenuPosition {
    top: number;
    left: number;
    maxHeight?: number;
    transformOrigin: string;
}

function openGoogleSearch(text: string) {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(text)}`, "_blank");
}

function calculateMenuPosition(
    triggerX: number,
    triggerY: number,
    menuWidth: number,
    menuHeight: number,
    viewportWidth: number = window.innerWidth,
    viewportHeight: number = window.innerHeight,
    padding: number = 8
): MenuPosition {
    let left = triggerX;
    let top = triggerY;
    let maxHeight: number | undefined;
    let transformOrigin = "top left";

    // Check horizontal overflow and adjust
    if (left + menuWidth + padding > viewportWidth) {
        // Try to position to the left of cursor
        left = triggerX - menuWidth;
        transformOrigin = "top right";

        // If still overflows on the left, position at edge
        if (left < padding) {
            left = viewportWidth - menuWidth - padding;
            transformOrigin = "top right";
        }
    }

    // Ensure minimum left position
    if (left < padding) {
        left = padding;
        transformOrigin = "top left";
    }

    // Check vertical overflow and adjust
    if (top + menuHeight + padding > viewportHeight) {
        // Try to position above cursor
        top = triggerY - menuHeight;
        transformOrigin = transformOrigin.replace("top", "bottom");

        // If still overflows at top, position at edge with height limit
        if (top < padding) {
            top = padding;
            maxHeight = viewportHeight - padding * 2;
            transformOrigin = "top " + transformOrigin.split(" ")[1];
        }
    }

    // Ensure minimum top position
    if (top < padding) {
        top = padding;
        transformOrigin = "top " + transformOrigin.split(" ")[1];
    }

    return { top, left, maxHeight, transformOrigin };
}

function calculateSubmenuPosition(
    parentElement: HTMLElement,
    submenuWidth: number,
    submenuHeight: number,
    viewportWidth: number = window.innerWidth,
    viewportHeight: number = window.innerHeight,
    padding: number = 8
): { position: MenuPosition; className: string } {
    const parentRect = parentElement.getBoundingClientRect();
    let left = parentRect.right + 4; // 4px gap
    let top = parentRect.top;
    let maxHeight: number | undefined;
    let transformOrigin = "top left";
    let className = "left-full translate-x-1";

    // Check if submenu overflows on the right
    if (left + submenuWidth + padding > viewportWidth) {
        // Position to the left of parent
        left = parentRect.left - submenuWidth - 4;
        transformOrigin = "top right";
        className = "right-full -translate-x-1";

        // If still overflows on the left, position at edge
        if (left < padding) {
            left = padding;
            transformOrigin = "top left";
            className = "left-0";
        }
    }

    // Check vertical overflow
    if (top + submenuHeight + padding > viewportHeight) {
        // Try to align bottom of submenu with bottom of parent
        top = parentRect.bottom - submenuHeight;
        transformOrigin = transformOrigin.replace("top", "bottom");

        // If still overflows at top, limit height
        if (top < padding) {
            top = padding;
            maxHeight = viewportHeight - padding * 2;
            transformOrigin = "top " + transformOrigin.split(" ")[1];
        }
    }

    return {
        position: { top, left, maxHeight, transformOrigin },
        className
    };
}

export default function GlobalContextMenu() {
    const [state, setState] = React.useState<MenuState>(() => ({
        items: null,
        position: { x: 0, y: 0 },
        visible: false,
    }));

    const menuRef = React.useRef<HTMLUListElement>(null);
    const [menuPosition, setMenuPosition] = React.useState<MenuPosition>({
        top: 0,
        left: 0,
        transformOrigin: "top left"
    });

    const close = React.useCallback(() => {
        setState((prev) => ({ ...prev, visible: false, items: null }));
    }, []);

    // Calculate menu position when menu becomes visible
    React.useEffect(() => {
        if (state.visible && menuRef.current) {
            const menuElement = menuRef.current;
            const menuRect = menuElement.getBoundingClientRect();

            const position = calculateMenuPosition(
                state.position.x,
                state.position.y,
                menuRect.width || 200, // fallback width
                menuRect.height || 300, // fallback height
            );

            setMenuPosition(position);
        }
    }, [state.visible, state.position, state.items]);

    React.useEffect(() => {
        function openHandler(
            event: CustomEvent<{ items: ContextMenuItem[]; position: { x: number; y: number } }>,
        ) {
            // Clone original items so we don't mutate upstream state.
            const items: ContextMenuItem[] = [...event.detail.items];

            // If the user currently has some text selected, prepend a "Copy Text" option.
            const selectedText = window.getSelection()?.toString() ?? "";
            if (selectedText.trim().length > 0) {
                const hasCopy = items.some(
                    (i) => i.type === "item" && (i as any).label === "Copy Text",
                );
                if (!hasCopy) {
                    items.unshift(
                        {
                            type: "item",
                            label: "Copy Text",
                            icon: <IconCopy className="size-4" />,
                            onClick: () => {
                                const text = window.getSelection()?.toString() ?? "";
                                if (text) {
                                    void navigator.clipboard.writeText(text);
                                }
                            },
                        },
                        {
                            type: "item",
                            label: "Ask AI",
                            icon: <IconCopy className="size-4" />,
                            onClick: () => {
                                const text = window.getSelection()?.toString() ?? "";
                                if (text) {
                                    window.askQuestion(text);
                                }
                            },
                        },
                        {
                            type: "item",
                            label: "Google Search",
                            icon: <IconCopy className="size-4" />,
                            onClick: () => {
                                const text = window.getSelection()?.toString() ?? "";
                                if (text) {
                                    openGoogleSearch(text);
                                }
                            },
                        },
                        {
                            type: "separator",
                        }
                    );

                }
            }

            setState({
                items,
                position: event.detail.position,
                visible: true,
            });
        }

        window.addEventListener(
            OPEN_CONTEXT_MENU_EVENT,
            openHandler as EventListener,
        );
        window.addEventListener("click", close);
        // Close the menu on right click anywhere else so it toggles.
        window.addEventListener("contextmenu", close);

        const keyHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", keyHandler);

        return () => {
            window.removeEventListener(
                OPEN_CONTEXT_MENU_EVENT,
                openHandler as EventListener,
            );
            window.removeEventListener("click", close);
            window.removeEventListener("contextmenu", close);
            window.removeEventListener("keydown", keyHandler);
        };
    }, [close]);

    if (!state.visible || !state.items) return null;

    const { x, y } = state.position;

    return (<>
        <div style={{ top: y - 4, left: x - 4 }}
            className="absolute top-0 z-[9998] left-0 w-2 h-2" />
        <div className="fixed inset-0 z-[9999] pointer-events-none select-none">
            <ul
                ref={menuRef}
                style={{
                    top: menuPosition.top,
                    left: menuPosition.left,
                    maxHeight: menuPosition.maxHeight,
                    transformOrigin: menuPosition.transformOrigin
                }}
                className={cn(
                    "absolute pointer-events-auto min-w-[200px] rounded-md border bg-popover text-popover-foreground shadow-md p-1",
                    menuPosition.maxHeight && "overflow-y-auto"
                )}
            >
                {state.items.map((item, idx) => (
                    <MenuItemRenderer key={idx} item={item} close={close} />
                ))}
            </ul>
        </div>
    </>
    );
}

interface ItemRendererProps {
    item: ContextMenuItem;
    close: () => void;
}

function MenuItemRenderer({ item, close }: ItemRendererProps) {
    const className = item.className;
    const isDisabled = (item as any).isDisabled ?? (item as any).disabled ?? false;
    const submenuRef = React.useRef<HTMLUListElement>(null);
    const parentRef = React.useRef<HTMLLIElement>(null);
    const [submenuStyle, setSubmenuStyle] = React.useState<React.CSSProperties>({});
    const [submenuClasses, setSubmenuClasses] = React.useState("left-full translate-x-1");

    const handleSubmenuShow = React.useCallback(() => {
        if (item.type === "dropdown" && parentRef.current) {
            const parentElement = parentRef.current;
            const parentRect = parentElement.getBoundingClientRect();

            // Calculate submenu position
            const submenuWidth = 200; // estimated width
            const submenuHeight = item.children.length * 40; // estimated height

            const result = calculateSubmenuPosition(
                parentElement,
                submenuWidth,
                submenuHeight
            );

            setSubmenuStyle({
                top: result.position.top - parentRect.top,
                left: result.position.left - parentRect.left,
                maxHeight: result.position.maxHeight,
                transformOrigin: result.position.transformOrigin
            });
            setSubmenuClasses(result.className);
        }
    }, [item]);

    switch (item.type) {
        case "separator":
            return <li className={cn("-mx-1 my-1 h-px bg-muted", className)} />;
        case "header":
            return (
                <li className={cn("px-3 py-1.5 text-xs font-semibold text-muted-foreground select-none", className)}>
                    {item.label}
                </li>
            );
        case "item":
            return (
                <li
                    title={item.tooltip}
                    className={cn(
                        "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                        isDisabled
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer hover:bg-accent hover:text-accent-foreground",
                        className
                    )}
                    onClick={() => {
                        if (isDisabled) return;
                        item.onClick();
                        close();
                    }}
                    aria-disabled={isDisabled}
                >
                    {item.icon && <span className="size-4">{item.icon}</span>}
                    <span>{item.label}</span>
                </li>
            );
        case "dropdown":
            return (
                <li ref={parentRef} className={cn("relative group/submenu select-none", className)}>
                    <div
                        title={item.tooltip}
                        className={cn(
                            "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                            isDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer hover:bg-accent hover:text-accent-foreground"
                        )}
                        onMouseEnter={handleSubmenuShow}
                        aria-disabled={isDisabled}
                    >
                        {item.icon && <span className="size-4">{item.icon}</span>}
                        <span>{item.label}</span>
                        <ChevronRight className="ml-auto size-4 opacity-60 group-hover/submenu:opacity-100" />
                    </div>
                    {!isDisabled && (
                        <ul
                            ref={submenuRef}
                            style={submenuStyle}
                            className={cn(
                                "absolute hidden min-w-[200px] group-hover/submenu:block rounded-md border bg-popover p-1 shadow-lg",
                                submenuStyle.maxHeight && "overflow-y-auto",
                                submenuClasses
                            )}
                        >
                            {item.children.map((child, idx) => (
                                <MenuItemRenderer key={idx} item={child} close={close} />
                            ))}
                        </ul>
                    )}
                </li>
            );
        default:
            return null;
    }
}